// Part of taxPages, split by screen to keep files short; ../taxPages.ts merges the parts.
// Keys sit directly under taxPages, in sub-objects whose names no other part uses.
// India GST screens (document 06 T6–T12). GSTR-1 / GSTR-3B / IMS table codes stay in Latin.
export const en = {
  gst: {
    title: 'GST',
    description: 'GSTR-1 and GSTR-3B for each month, computed from the books.',
    imsLink: 'IMS and GSTR-2B',
    ewayLink: 'E-way bills',
    indiaOnlyTitle: 'GST applies to India clients',
    indiaOnlyHint: 'This client files UAE VAT returns.',
    loadError: 'Could not load GST returns',
    notRegistered: 'Not registered: add the GSTIN in Company settings',
    colDocuments: 'Documents',
    colOutwardTaxable: 'Outward taxable',
    colOutputTax: 'Output tax',
    colItc: 'Input tax credit',
    colCashPayable: 'Cash payable',
    exceptions_one: '{{count}} exception',
    exceptions_other: '{{count}} exceptions',
    reports: 'GST reports',
    openAsReport: 'Open as report',
    downloadJson: 'Download JSON',
    addGstin: 'Add the GSTIN in Company settings',
    dueOn: '· due {{date}}',
    returnPeriod: 'Return period',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    taxableValue: 'Taxable value',
    cess: 'Cess',
    placeOfSupply: 'Place of supply',
    value: 'Value',
    interState: 'Inter-state',
    intraState: 'Intra-state',
    natureOfSupplies: 'Nature of supplies',
    mode: 'Mode',
  },
  gstr1: {
    description: 'Outward supplies for the month, by GSTR-1 table, from the posted documents.',
    loadError: 'Could not compute GSTR-1',
    exceptionsToReview_one: '{{count}} exception to review before filing',
    exceptionsToReview_other: '{{count}} exceptions to review before filing',
    summary: 'Summary',
    colTable: 'Table',
    colSupplies: 'Supplies',
    colCount: 'Count',
    colSupply: 'Supply',
    colRate: 'Rate',
    colNilRated: 'Nil rated',
    colExempted: 'Exempted',
    colNonGst: 'Non-GST',
    colUqc: 'UQC',
    colQuantity: 'Quantity',
    colNature: 'Nature of document',
    colFrom: 'From',
    colTo: 'To',
    colTotal: 'Total',
    colCancelled: 'Cancelled',
    colNetIssued: 'Net issued',
    section: {
      b2b: '4A · B2B invoices',
      b2cl: '5 · B2C (large) invoices',
      exp: '6A · Exports',
      b2cs: '7 · B2C (others)',
      nil: '8 · Nil rated, exempted and non-GST supplies',
      cdnr: '9B · Credit and debit notes (registered)',
      cdnur: '9B · Credit and debit notes (unregistered)',
      hsn: '12 · HSN/SAC summary ({{kind}})',
      docs: '13 · Documents issued',
    },
    nil: {
      INTRB2B: 'Inter-state supplies to registered persons',
      INTRAB2B: 'Intra-state supplies to registered persons',
      INTRB2C: 'Inter-state supplies to unregistered persons',
      INTRAB2C: 'Intra-state supplies to unregistered persons',
    },
    // The summary rows' labels as the API sends them (packages/tax-packs/src/in/gstr1.ts).
    summaryRow: {
      b2b: 'B2B invoices',
      b2cl: 'B2C (large) invoices',
      exp: 'Exports',
      b2cs: 'B2C (others)',
      cdnr: 'Credit/debit notes (registered)',
      cdnur: 'Credit/debit notes (unregistered)',
      nil: 'Nil rated, exempted and non-GST supplies',
    },
    // Table 13 document types by GSTN doc_num.
    docType: {
      d1: 'Invoices for outward supply',
      d5: 'Credit notes',
      d9: 'Delivery challans for job work',
      d10: 'Delivery challans for supply on approval',
      d11: 'Delivery challans in case of liquid gas',
      d12: 'Delivery challans in cases other than by way of supply',
    },
  },
  gstr3b: {
    description:
      'Summary return for the month: outward supplies, input tax credit and tax payable.',
    loadError: 'Could not compute GSTR-3B',
    // GSTR-3B rows by key (packages/tax-packs/src/in/gstr3b.ts GSTR3B_LABELS).
    row: {
      r31a: 'Outward taxable supplies (other than zero rated, nil rated and exempted)',
      r31b: 'Outward taxable supplies (zero rated)',
      r31c: 'Other outward supplies (nil rated, exempted)',
      r31d: 'Inward supplies (liable to reverse charge)',
      r31e: 'Non-GST outward supplies',
      r4A1: 'Import of goods',
      r4A2: 'Import of services',
      r4A3: 'Inward supplies liable to reverse charge (other than 1 and 2 above)',
      r4A4: 'Inward supplies from ISD',
      r4A5: 'All other ITC',
      r4B1: 'ITC reversed: as per rules 38, 42 and 43 of CGST Rules and section 17(5)',
      r4B2: 'ITC reversed: others',
      r4C: 'Net ITC available (A) − (B)',
      r4D1: 'ITC reclaimed which was reversed under Table 4(B)(2) in earlier tax period',
      r4D2: 'Ineligible ITC under section 16(4) and ITC restricted due to PoS rules',
    },
    s31: '3.1 · Outward supplies and inward supplies liable to reverse charge',
    s32: '3.2 · Inter-state supplies to unregistered persons',
    noneThisMonth: 'None this month.',
    s4: '4 · Eligible input tax credit',
    s4Note:
      'ITC reversals (4B) and ineligible ITC (4D) are not derived from the books; enter them on the portal. Match the input tax credit with GSTR-2B before filing.',
    s5: '5 · Exempt, nil rated and non-GST inward supplies',
    compositionExempt: 'Composition, exempt and nil rated supplies',
    nonGstSupplies: 'Non-GST supplies',
    s61: '6.1 · Payment of tax',
    taxPayable: 'Tax payable (other than reverse charge)',
    paidItcIgst: 'Paid through ITC: IGST',
    paidItcCgst: 'Paid through ITC: CGST',
    paidItcSgst: 'Paid through ITC: SGST/UTGST',
    paidCash: 'Paid in cash (including reverse charge)',
    itcCarried: 'ITC carried forward',
  },
  ims: {
    description:
      'Supplier invoices and notes from the GST portal, matched to your bills, with the action to take on each.',
    tooLarge: "{{name}} is over 4.5 MB. Import one month's GSTR-2B at a time.",
    imported: '{{n}} supplier documents for {{month}} imported and matched.',
    fileAria: 'GSTR-2B or CSV file',
    importing: 'Importing…',
    importButton: 'Import GSTR-2B',
    indiaOnly: 'IMS applies to India clients',
    rejectPrompt: 'Why reject? (kept with the action)',
    reasonTooLong: 'Keep the reason to 200 characters.',
    markedAccept_one: '{{count}} document marked accepted.',
    markedAccept_other: '{{count}} documents marked accepted.',
    markedReject_one: '{{count}} document marked rejected.',
    markedReject_other: '{{count}} documents marked rejected.',
    markedPending_one: '{{count}} document marked pending.',
    markedPending_other: '{{count}} documents marked pending.',
    markedNoAction_one: '{{count}} document marked no action.',
    markedNoAction_other: '{{count}} documents marked no action.',
    noImportTitle: 'No GSTR-2B imported for {{month}}',
    noImportHint:
      'On the GST portal open Returns › GSTR-2B for the month and download the JSON, then use Import GSTR-2B. Or fill in the CSV template from your supplier statements.',
    csvTemplate: 'CSV template',
    worksheetNote:
      "This is a worksheet. No GSP is connected, so these actions are not sent to the GST portal: take them on the portal's IMS dashboard before GSTR-2B is generated, using the downloaded list. Documents with no action are deemed accepted.",
    match: {
      matched: 'Matched',
      probable: 'Probable match',
      mismatch: 'Mismatch',
      notInBooks: 'Not in books',
    },
    action: {
      accept: 'Accepted',
      reject: 'Rejected',
      pending: 'Pending',
      noAction: 'No action',
    },
    chip: {
      probable: 'Probable',
      open: 'No action yet',
    },
    notOnPortal: 'Not on portal',
    itcCompare: 'ITC per GSTR-2B · in books',
    filterAria: 'Filter',
    matchAgain: 'Match again',
    downloadWorksheet: 'Download worksheet',
    selected: '{{n}} selected',
    accept: 'Accept',
    keepPending: 'Keep pending',
    reject: 'Reject',
    clearAction: 'Clear action',
    selectAll: 'Select all',
    selectRow: 'Select {{number}}',
    colSupplier: 'Supplier',
    colTaxable: 'Taxable',
    colMatch: 'Match',
    colInBooks: 'In books',
    colAction: 'Action',
    colVendor: 'Vendor',
    colBillNo: 'Bill no.',
    notReportedTitle: 'Bills your suppliers have not reported',
    notReportedHint:
      'Their input tax credit is not in GSTR-2B until the supplier files them; follow up with the supplier.',
    lastImport: 'Last import: {{file}} · {{rows}} documents',
    difference: '{{field}}: portal {{portal}}, books {{books}}',
    docInvoice: 'Invoice',
    docCreditNote: 'Credit note',
    docDebitNote: 'Debit note',
    itcNotAvailable: 'ITC not available',
    // Difference field names: English shows the API's field identifiers as they are.
    field: {
      docType: 'docType',
      number: 'number',
      date: 'date',
      taxable: 'taxable',
      igst: 'igst',
      cgst: 'cgst',
      sgst: 'sgst',
      cess: 'cess',
      total: 'total',
    },
  },
  ewayList: {
    description:
      'Generated from approved invoices and delivery challans; a bill is cancelled within 24 hours, or its vehicle updated while it is valid.',
    indiaOnly: 'E-way bills apply to India clients',
    active: 'Active',
    expired: 'Expired',
    cancelled: 'Cancelled',
    empty: 'No e-way bills here',
    emptyHint: 'Generate one from an approved invoice or a delivery challan.',
    colEwbNo: 'EWB no.',
    colGenerated: 'Generated',
    colDocument: 'Document',
    colValidUntil: 'Valid until',
    colVehicle: 'Vehicle',
    partAOnly: 'Part A only',
  },
  irn: {
    title: 'E-invoice (IRP)',
    testIrn: 'Test IRN',
    generatedLine: '<b>IRN generated</b> · Ack. no. <n>{{ackNo}}</n> on {{when}}',
    locked:
      'This document can no longer be edited or voided; cancel the IRN within 24 hours or issue a credit note.',
    cancelledLine: '<b>IRN cancelled</b> on {{when}} · {{reason}}',
    editableAgain:
      'The document can be edited or voided again. Generate a new IRN once it is correct.',
    fixFirst: 'Fix what the IRP rules find below the form, then generate a new IRN.',
    notRegistered: 'Not registered with the IRP yet.',
    notReady: 'Not ready for the IRP:',
    line: 'Line {{line}}:',
    toastGenerated: 'IRN generated for {{number}} (ack. {{ackNo}}).',
    theDocument: 'the document',
    generateNew: 'Generate new IRN',
    generate: 'Generate IRN',
    cancel: 'Cancel IRN',
    cancelHint:
      'The IRP allows cancelling an IRN within 24 hours. After that, issue a credit note.',
    remarkPlaceholder: 'Wrong place of supply',
    toastCancelled: 'IRN cancelled.',
  },
  ctExport: {
    fileName: 'Corporate tax {{label}}.csv',
  },
};

export const ar = {
  gst: {
    title: 'ضريبة السلع والخدمات (GST)',
    description: 'إقرارا GSTR-1 وGSTR-3B لكل شهر، محسوبان من الدفاتر.',
    imsLink: 'IMS وGSTR-2B',
    ewayLink: 'بوالص الشحن الإلكترونية',
    indiaOnlyTitle: 'ضريبة السلع والخدمات تنطبق على عملاء الهند',
    indiaOnlyHint: 'يقدّم هذا العميل إقرارات ضريبة القيمة المضافة في الإمارات.',
    loadError: 'تعذّر تحميل إقرارات ضريبة السلع والخدمات',
    notRegistered: 'غير مسجّل: أضف رقم GSTIN في إعدادات الشركة',
    colDocuments: 'المستندات',
    colOutwardTaxable: 'التوريدات الخارجة الخاضعة للضريبة',
    colOutputTax: 'ضريبة المخرجات',
    colItc: 'رصيد ضريبة المدخلات',
    colCashPayable: 'المستحق نقدًا',
    exceptions_zero: 'لا استثناءات',
    exceptions_one: 'استثناء واحد',
    exceptions_two: 'استثناءان',
    exceptions_few: '{{count}} استثناءات',
    exceptions_many: '{{count}} استثناءً',
    exceptions_other: '{{count}} استثناء',
    reports: 'تقارير ضريبة السلع والخدمات',
    openAsReport: 'فتح كتقرير',
    downloadJson: 'تنزيل JSON',
    addGstin: 'أضف رقم GSTIN في إعدادات الشركة',
    dueOn: '· الاستحقاق {{date}}',
    returnPeriod: 'فترة الإقرار',
    previousMonth: 'الشهر السابق',
    nextMonth: 'الشهر التالي',
    taxableValue: 'القيمة الخاضعة للضريبة',
    cess: 'الرسم الإضافي (Cess)',
    placeOfSupply: 'مكان التوريد',
    value: 'القيمة',
    interState: 'بين الولايات',
    intraState: 'داخل الولاية',
    natureOfSupplies: 'طبيعة التوريدات',
    mode: 'وسيلة النقل',
  },
  gstr1: {
    description: 'التوريدات الخارجة للشهر، حسب جداول GSTR-1، من المستندات المرحّلة.',
    loadError: 'تعذّر احتساب GSTR-1',
    exceptionsToReview_zero: 'لا استثناءات للمراجعة قبل التقديم',
    exceptionsToReview_one: 'استثناء واحد للمراجعة قبل التقديم',
    exceptionsToReview_two: 'استثناءان للمراجعة قبل التقديم',
    exceptionsToReview_few: '{{count}} استثناءات للمراجعة قبل التقديم',
    exceptionsToReview_many: '{{count}} استثناءً للمراجعة قبل التقديم',
    exceptionsToReview_other: '{{count}} استثناء للمراجعة قبل التقديم',
    summary: 'الملخص',
    colTable: 'الجدول',
    colSupplies: 'التوريدات',
    colCount: 'العدد',
    colSupply: 'التوريد',
    colRate: 'النسبة',
    colNilRated: 'معدومة النسبة (Nil)',
    colExempted: 'معفاة',
    colNonGst: 'خارج نطاق GST',
    colUqc: 'UQC',
    colQuantity: 'الكمية',
    colNature: 'طبيعة المستند',
    colFrom: 'من',
    colTo: 'إلى',
    colTotal: 'الإجمالي',
    colCancelled: 'الملغاة',
    colNetIssued: 'صافي المُصدَر',
    section: {
      b2b: '4A · فواتير B2B',
      b2cl: '5 · فواتير B2C (الكبيرة)',
      exp: '6A · الصادرات',
      b2cs: '7 · B2C (أخرى)',
      nil: '8 · التوريدات معدومة النسبة والمعفاة وغير الخاضعة لـ GST',
      cdnr: '9B · الإشعارات الدائنة والمدينة (المسجّلون)',
      cdnur: '9B · الإشعارات الدائنة والمدينة (غير المسجّلين)',
      hsn: '12 · ملخص HSN/SAC ({{kind}})',
      docs: '13 · المستندات المُصدَرة',
    },
    nil: {
      INTRB2B: 'توريدات بين الولايات إلى أشخاص مسجّلين',
      INTRAB2B: 'توريدات داخل الولاية إلى أشخاص مسجّلين',
      INTRB2C: 'توريدات بين الولايات إلى أشخاص غير مسجّلين',
      INTRAB2C: 'توريدات داخل الولاية إلى أشخاص غير مسجّلين',
    },
    summaryRow: {
      b2b: 'فواتير B2B',
      b2cl: 'فواتير B2C (الكبيرة)',
      exp: 'الصادرات',
      b2cs: 'B2C (أخرى)',
      cdnr: 'الإشعارات الدائنة/المدينة (المسجّلون)',
      cdnur: 'الإشعارات الدائنة/المدينة (غير المسجّلين)',
      nil: 'التوريدات معدومة النسبة والمعفاة وغير الخاضعة لـ GST',
    },
    docType: {
      d1: 'فواتير التوريدات الخارجة',
      d5: 'الإشعارات الدائنة',
      d9: 'إذونات التسليم للتشغيل للغير',
      d10: 'إذونات التسليم للتوريد بشرط القبول',
      d11: 'إذونات التسليم في حالة الغاز المسال',
      d12: 'إذونات التسليم في غير حالات التوريد',
    },
  },
  gstr3b: {
    description: 'إقرار ملخّص للشهر: التوريدات الخارجة ورصيد ضريبة المدخلات والضريبة المستحقة.',
    loadError: 'تعذّر احتساب GSTR-3B',
    row: {
      r31a: 'التوريدات الخارجة الخاضعة للضريبة (عدا الخاضعة لنسبة الصفر ومعدومة النسبة والمعفاة)',
      r31b: 'التوريدات الخارجة الخاضعة للضريبة (الخاضعة لنسبة الصفر)',
      r31c: 'توريدات خارجة أخرى (معدومة النسبة، معفاة)',
      r31d: 'التوريدات الداخلة (الخاضعة للاحتساب العكسي)',
      r31e: 'التوريدات الخارجة غير الخاضعة لـ GST',
      r4A1: 'استيراد السلع',
      r4A2: 'استيراد الخدمات',
      r4A3: 'التوريدات الداخلة الخاضعة للاحتساب العكسي (عدا 1 و2 أعلاه)',
      r4A4: 'التوريدات الداخلة من ISD',
      r4A5: 'جميع أرصدة ITC الأخرى',
      r4B1: 'ITC المعكوس: وفق القواعد 38 و42 و43 من قواعد CGST والمادة 17(5)',
      r4B2: 'ITC المعكوس: أخرى',
      r4C: 'صافي ITC المتاح (A) − (B)',
      r4D1: 'ITC المسترد الذي عُكس بموجب الجدول 4(B)(2) في فترة ضريبية سابقة',
      r4D2: 'ITC غير المؤهل بموجب المادة 16(4) وITC المقيّد بسبب قواعد مكان التوريد',
    },
    s31: '3.1 · التوريدات الخارجة والتوريدات الداخلة الخاضعة للاحتساب العكسي',
    s32: '3.2 · التوريدات بين الولايات إلى أشخاص غير مسجّلين',
    noneThisMonth: 'لا شيء هذا الشهر.',
    s4: '4 · رصيد ضريبة المدخلات المؤهل',
    s4Note:
      'لا تُستخرج عكوسات ITC (4B) وITC غير المؤهل (4D) من الدفاتر؛ أدخلها على البوابة. طابِق رصيد ضريبة المدخلات مع GSTR-2B قبل التقديم.',
    s5: '5 · التوريدات الداخلة المعفاة ومعدومة النسبة وغير الخاضعة لـ GST',
    compositionExempt: 'توريدات نظام التسوية والمعفاة ومعدومة النسبة',
    nonGstSupplies: 'التوريدات غير الخاضعة لـ GST',
    s61: '6.1 · سداد الضريبة',
    taxPayable: 'الضريبة المستحقة (عدا الاحتساب العكسي)',
    paidItcIgst: 'مسدّدة من ITC: IGST',
    paidItcCgst: 'مسدّدة من ITC: CGST',
    paidItcSgst: 'مسدّدة من ITC: SGST/UTGST',
    paidCash: 'مسدّدة نقدًا (بما فيها الاحتساب العكسي)',
    itcCarried: 'ITC المرحّل',
  },
  ims: {
    description:
      'فواتير الموردين وإشعاراتهم من بوابة GST، مطابَقةً مع فواتير الشراء لديك، مع الإجراء المطلوب لكل منها.',
    tooLarge: 'حجم {{name}} يتجاوز 4.5 ميغابايت. استورد GSTR-2B لشهر واحد في كل مرة.',
    imported: 'استُورد {{n}} مستند مورد لشهر {{month}} وطوبِق.',
    fileAria: 'ملف GSTR-2B أو CSV',
    importing: 'جارٍ الاستيراد…',
    importButton: 'استيراد GSTR-2B',
    indiaOnly: 'نظام IMS ينطبق على عملاء الهند',
    rejectPrompt: 'لماذا الرفض؟ (يُحفظ مع الإجراء)',
    reasonTooLong: 'اجعل السبب 200 حرف كحد أقصى.',
    markedAccept_zero: 'لم يُحدَّد أي مستند كمقبول.',
    markedAccept_one: 'حُدّد مستند واحد كمقبول.',
    markedAccept_two: 'حُدّد مستندان كمقبولين.',
    markedAccept_few: 'حُدّدت {{count}} مستندات كمقبولة.',
    markedAccept_many: 'حُدّد {{count}} مستندًا كمقبول.',
    markedAccept_other: 'حُدّد {{count}} مستند كمقبول.',
    markedReject_zero: 'لم يُحدَّد أي مستند كمرفوض.',
    markedReject_one: 'حُدّد مستند واحد كمرفوض.',
    markedReject_two: 'حُدّد مستندان كمرفوضين.',
    markedReject_few: 'حُدّدت {{count}} مستندات كمرفوضة.',
    markedReject_many: 'حُدّد {{count}} مستندًا كمرفوض.',
    markedReject_other: 'حُدّد {{count}} مستند كمرفوض.',
    markedPending_zero: 'لم يُحدَّد أي مستند كمعلّق.',
    markedPending_one: 'حُدّد مستند واحد كمعلّق.',
    markedPending_two: 'حُدّد مستندان كمعلّقين.',
    markedPending_few: 'حُدّدت {{count}} مستندات كمعلّقة.',
    markedPending_many: 'حُدّد {{count}} مستندًا كمعلّق.',
    markedPending_other: 'حُدّد {{count}} مستند كمعلّق.',
    markedNoAction_zero: 'لم يُحدَّد أي مستند بلا إجراء.',
    markedNoAction_one: 'حُدّد مستند واحد بلا إجراء.',
    markedNoAction_two: 'حُدّد مستندان بلا إجراء.',
    markedNoAction_few: 'حُدّدت {{count}} مستندات بلا إجراء.',
    markedNoAction_many: 'حُدّد {{count}} مستندًا بلا إجراء.',
    markedNoAction_other: 'حُدّد {{count}} مستند بلا إجراء.',
    noImportTitle: 'لم يُستورد GSTR-2B لشهر {{month}}',
    noImportHint:
      'على بوابة GST افتح Returns › GSTR-2B للشهر ونزّل ملف JSON، ثم استخدم «استيراد GSTR-2B». أو املأ قالب CSV من كشوف حسابات الموردين.',
    csvTemplate: 'قالب CSV',
    worksheetNote:
      'هذه ورقة عمل. لا يوجد مزوّد GSP مربوط، لذلك لا تُرسل هذه الإجراءات إلى بوابة GST: نفّذها على لوحة IMS في البوابة قبل إنشاء GSTR-2B، مستعينًا بالقائمة المنزّلة. المستندات التي لا إجراء عليها تُعدّ مقبولة.',
    match: {
      matched: 'مطابَق',
      probable: 'مطابقة محتملة',
      mismatch: 'غير مطابق',
      notInBooks: 'غير موجود في الدفاتر',
    },
    action: {
      accept: 'مقبول',
      reject: 'مرفوض',
      pending: 'معلّق',
      noAction: 'بلا إجراء',
    },
    chip: {
      probable: 'محتملة',
      open: 'بلا إجراء بعد',
    },
    notOnPortal: 'غير موجود على البوابة',
    itcCompare: 'ITC وفق GSTR-2B · في الدفاتر',
    filterAria: 'تصفية',
    matchAgain: 'المطابقة مجددًا',
    downloadWorksheet: 'تنزيل ورقة العمل',
    selected: 'محدد: {{n}}',
    accept: 'قبول',
    keepPending: 'إبقاء معلّق',
    reject: 'رفض',
    clearAction: 'مسح الإجراء',
    selectAll: 'تحديد الكل',
    selectRow: 'تحديد {{number}}',
    colSupplier: 'المورد',
    colTaxable: 'الخاضع للضريبة',
    colMatch: 'المطابقة',
    colInBooks: 'في الدفاتر',
    colAction: 'الإجراء',
    colVendor: 'المورد',
    colBillNo: 'رقم فاتورة الشراء',
    notReportedTitle: 'فواتير شراء لم يُبلغ عنها الموردون',
    notReportedHint:
      'لا يظهر رصيد ضريبة المدخلات الخاص بها في GSTR-2B حتى يقدّمها المورد؛ تابِع الأمر مع المورد.',
    lastImport: 'آخر استيراد: {{file}} · {{rows}} مستند',
    difference: '{{field}}: البوابة {{portal}}، الدفاتر {{books}}',
    docInvoice: 'فاتورة',
    docCreditNote: 'إشعار دائن',
    docDebitNote: 'إشعار مدين',
    itcNotAvailable: 'ITC غير متاح',
    field: {
      docType: 'نوع المستند',
      number: 'الرقم',
      date: 'التاريخ',
      taxable: 'الخاضع للضريبة',
      igst: 'IGST',
      cgst: 'CGST',
      sgst: 'SGST',
      cess: 'Cess',
      total: 'الإجمالي',
    },
  },
  ewayList: {
    description:
      'تُنشأ من الفواتير المعتمدة وإذونات التسليم؛ تُلغى البوليصة خلال 24 ساعة، أو تُحدَّث مركبتها ما دامت صالحة.',
    indiaOnly: 'بوالص الشحن الإلكترونية تنطبق على عملاء الهند',
    active: 'سارية',
    expired: 'منتهية',
    cancelled: 'ملغاة',
    empty: 'لا توجد بوالص شحن هنا',
    emptyHint: 'أنشئ واحدة من فاتورة معتمدة أو إذن تسليم.',
    colEwbNo: 'رقم EWB',
    colGenerated: 'تاريخ الإنشاء',
    colDocument: 'المستند',
    colValidUntil: 'صالحة حتى',
    colVehicle: 'المركبة',
    partAOnly: 'الجزء A فقط',
  },
  irn: {
    title: 'الفاتورة الإلكترونية (IRP)',
    testIrn: 'IRN تجريبي',
    generatedLine: '<b>أُنشئ IRN</b> · رقم إشعار الاستلام <n>{{ackNo}}</n> في {{when}}',
    locked:
      'لم يعد بالإمكان تعديل هذا المستند أو إبطاله؛ ألغِ IRN خلال 24 ساعة أو أصدر إشعارًا دائنًا.',
    cancelledLine: '<b>أُلغي IRN</b> في {{when}} · {{reason}}',
    editableAgain: 'يمكن تعديل المستند أو إبطاله مجددًا. أنشئ IRN جديدًا بعد تصحيحه.',
    fixFirst: 'صحّح ما تكشفه قواعد IRP أسفل النموذج، ثم أنشئ IRN جديدًا.',
    notRegistered: 'لم يُسجَّل لدى IRP بعد.',
    notReady: 'غير جاهز لـ IRP:',
    line: 'السطر {{line}}:',
    toastGenerated: 'أُنشئ IRN لـ {{number}} (إشعار الاستلام {{ackNo}}).',
    theDocument: 'المستند',
    generateNew: 'إنشاء IRN جديد',
    generate: 'إنشاء IRN',
    cancel: 'إلغاء IRN',
    cancelHint: 'تسمح IRP بإلغاء IRN خلال 24 ساعة. بعد ذلك، أصدر إشعارًا دائنًا.',
    remarkPlaceholder: 'مكان توريد خاطئ',
    toastCancelled: 'أُلغي IRN.',
  },
  ctExport: {
    fileName: 'ضريبة الشركات {{label}}.csv',
  },
};
